<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
      <div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Visitors Over Time</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">New vs returning visitors per day</p>
      </div>
      <div class="flex items-center gap-4">
        <span class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span> New
        </span>
        <span class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span class="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span> Returning
        </span>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading && !data" class="animate-pulse p-5 h-56 flex flex-col justify-end gap-2">
      <div class="flex items-end gap-1 h-40">
        <div v-for="i in 14" :key="i" class="flex-1 bg-gray-100 dark:bg-gray-800 rounded-t"
          :style="{ height: (30 + Math.sin(i) * 25 + i * 3) + '%' }"></div>
      </div>
      <div class="flex gap-1">
        <div v-for="i in 7" :key="i" class="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded"></div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"/>
        </svg>
      </div>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">No visitor data yet</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Visitor trends will appear once traffic data is collected.</p>
    </div>

    <!-- Chart -->
    <div v-else ref="chartEl" class="w-full h-56"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useAnalyticsTrafficStore } from 'stores/analyticsTraffic.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsTrafficStore()

const chartEl = ref(null)
const loading  = computed(() => store.visitorsOverTimeLoading)
const data     = computed(() => store.visitorsOverTime[props.duration] ?? null)
const isEmpty  = computed(() => {
  if (!data.value) return false
  return (data.value.total ?? []).every(v => v === 0)
})

let chart = null

function renderChart() {
  if (!data.value || !chartEl.value) return
  if (chart) { chart.destroy(); chart = null }

  const { labels, new: newV, returning } = data.value
  chart = Highcharts.chart(chartEl.value, {
    chart:  { type: 'area', height: 224, spacing: [16, 16, 16, 16] },
    xAxis:  {
      categories: labels,
      tickInterval: Math.ceil(labels.length / 7),
      crosshair: { color: '#e5e7eb', width: 1 },
    },
    yAxis:  { min: 0, allowDecimals: false },
    tooltip: { shared: true, crosshairs: true },
    plotOptions: {
      area: {
        fillOpacity: 0.08,
        marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true } } },
        lineWidth: 2,
      },
    },
    series: [
      { name: 'New',       data: newV,      color: '#10b981', fillColor: 'rgba(16,185,129,0.08)' },
      { name: 'Returning', data: returning, color: '#2563eb', fillColor: 'rgba(37,99,235,0.08)'  },
    ],
  })
}

watch(data, (val) => { if (val) nextTick(renderChart) })
onMounted(() => { if (data.value) renderChart() })
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
